import { zodResolver } from "@hookform/resolvers/zod";
import { KdsButton, KdsModal, KdsText } from "@kazaar-platform/design-system";
import { RiAddCircleFill } from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";

import {
  FileContext,
  FileContextProvider,
} from "@/components/FileUploadAsync/FileUploadAsync.context";
import { useToasts } from "@/hooks";
import { useOrderItems } from "@/oms/hooks/queries/useOrderItems";
import {
  type InferredModalUploadProofFormSchema,
  modalUploadProofFormSchema,
} from "./ModalUploadProofForm.schema";
import { type ModalUploadProofFormProps } from "./ModalUploadProofForm.types";
import { UploadProofItem } from "./UploadProofItem";

export const ModalUploadProofForm = ({
  orderId,
  orderItemId,
  isOpen,
  setOpenState,
  onError,
}: ModalUploadProofFormProps) => {
  // Hooks
  const { t } = useTranslate();
  const { add } = useToasts();

  // Add Proof Hooks
  const { useAddOrderItemProof } = useOrderItems();
  const { mutateAsync: addProofToOrderItem, isPending } =
    useAddOrderItemProof();

  // define Form
  const methods = useForm<InferredModalUploadProofFormSchema>({
    defaultValues: {
      attachments: [
        {
          fileSignedId: undefined,
          comment: undefined,
        },
      ],
    },
    resolver: zodResolver(modalUploadProofFormSchema),
  });
  // Define FieldArray
  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: "attachments",
  });

  // Handle OnSubmit
  const onSubmit = async (data: InferredModalUploadProofFormSchema) => {
    const res = await addProofToOrderItem({
      orderId,
      orderItemId,
      orderItemProofAddCommand: {
        attachments: data.attachments,
      },
    });

    if (res.status === 200 || res.status === 201) {
      handleOnSuccess();
    } else {
      handleOnError();
    }
  };

  // handleOnClose
  const handleOnClose = () => {
    methods.reset();
    setOpenState(false);
  };

  // Handle OnSucces
  const handleOnSuccess = () => {
    add({
      state: "success",
      title: t("modalUploadProof.toast.success"),
    });

    handleOnClose();
  };

  // Handle OnError
  const handleOnError = () => {
    if (onError) {
      onError();
    }

    add({
      state: "error",
      title: t("modalUploadProof.toast.error"),
    });

    setOpenState(false);
  };

  return (
    <FormProvider {...methods}>
      <FileContextProvider>
        <FileContext.Consumer>
          {(ctx) => (
            <KdsModal
              isOpen={isOpen}
              onOpenChange={handleOnClose}
              dialogProps={{
                dialogHeaderProps: {
                  title: t("modalUploadProof.title"),
                },
                content: (
                  <div className="flex flex-col gap-sp-md">
                    <div className="flex items-center justify-between gap-2">
                      <KdsText>{t("modalUploadProof.subtitle")}</KdsText>

                      <KdsButton
                        variant="outlined"
                        iconLeft={RiAddCircleFill}
                        color="neutral"
                        onPress={() =>
                          append({
                            fileSignedId: "",
                            comment: undefined,
                          })
                        }
                      >
                        {t("buttons.addProof")}
                      </KdsButton>
                    </div>

                    <div className="mb-4 flex max-h-[60vh] flex-col gap-6 overflow-auto p-1">
                      {fields.map((field, index) => (
                        <div key={field.id}>
                          {index !== 0 && (
                            <div className="border-grey-200 mb-6 border-t" />
                          )}

                          <UploadProofItem index={index} onRemove={remove} />
                        </div>
                      ))}
                    </div>
                  </div>
                ),
                dialogFooterProps: {
                  buttonProps: [
                    {
                      children: t("buttons.cancel"),
                      variant: "ghost",
                      color: "neutral",
                      isLoading: isPending,
                      disabled: isPending || ctx?.isUploading,
                      onPress: () => handleOnClose(),
                    },
                    {
                      children: t("buttons.sendToClient"),
                      type: "submit",
                      isLoading: isPending,
                      disabled: isPending || ctx?.isUploading,
                      dataCy: "submitProofButton",
                      onPress: () => void methods.handleSubmit(onSubmit)(),
                    },
                  ],
                },
              }}
            />
          )}
        </FileContext.Consumer>
      </FileContextProvider>
    </FormProvider>
  );
};
